import React, { useRef, useState, useEffect } from 'react';
import keyframes from '@ff/ui/utils/keyframes';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';

type PlayerState =
  'loading' |
  'ready' |
  'playing' |
  'paused' |
  'ended';

type PlayerAction =
  'start_playback' |
  'pause_playback';

interface Props {
  src: string;
}

export default function CustomVideo(props: Props) {
  const { src } = props;

  const ref = useRef<HTMLVideoElement>();
  const [playerState, setPlayerState] = useState<PlayerState>('loading');
  const [playerAction, setPlayerAction] = useState<PlayerAction>();

  /* Transition between states */
  useEffect(() => {
    // Reset action
    setPlayerAction(null);

    switch (playerAction) {
      case 'start_playback':
        // Start playback, transition to next state
        ref.current.play();
        setPlayerState('playing');
        break;
      case 'pause_playback':
        // Pause playback, transition to next state
        ref.current.pause();
        setPlayerState('paused');
      break;
    }
  }, [playerAction]);

  /* Handle video playback and loading events */
  useEffect(() => {
    // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#events
    const { current } = ref;

    // Video can be played
    const changeLoadingToReady = () => {
      setPlayerState((state) => state === 'loading' ? 'ready' : state);
    }
    current.addEventListener('canplay', changeLoadingToReady);

    // Video ended - show replay option
    const changeToEnded = () => {
      setPlayerState('ended');
    };
    current.addEventListener('ended', changeToEnded);

    return () => {
      if (current) {
        current.removeEventListener('canplay', changeLoadingToReady);
        current.removeEventListener('ended', changeToEnded);
      }
    };
  }, []);

  return (
    <Container>
      <video
        ref={ref}
        style={{
          maxWidth: '100%', // Limit video size
          display: 'block', // Remove 4px at bottom
        }}>
        <source src={src} />
      </video>
      <VideoOverlay
        isPlaying={playerState === 'playing'}
        onClick={() => {
          if (['playing'].includes(playerState)) {
            setPlayerAction('pause_playback')
          } else if (['ready', 'paused', 'ended'].includes(playerState)) {
            setPlayerAction('start_playback');
          }
        }}
      >
        {
          ['ended'].includes(playerState) &&
            <ReplayOverlay>
              <MaterialIcon style={{ fontSize: '60px' }}>replay</MaterialIcon>
            </ReplayOverlay>
        }
        {
          !['loading', 'ended'].includes(playerState) &&
          <ControlsBackground>
            <ControlsLayout>
              { ['playing'].includes(playerState) && <MaterialIcon>pause</MaterialIcon> }
              { ['ready', 'paused'].includes(playerState) && <MaterialIcon>play_arrow</MaterialIcon> }
            </ControlsLayout>
          </ControlsBackground>
        }
      </VideoOverlay>
    </Container>
  );
}


// Animations
const fadeInAnimation = keyframes({
  from: {
    opacity: '0'
  },
  to: {
    opacity: '1'
  }
});

const Container = styled(Box)({
  position: 'relative',
  color: 'white',
});

/** Overlay container for all controls and behavior */
const VideoOverlay = styled(Box)<{ isPlaying: boolean }>(({ isPlaying }) => ({
  // Cover the video
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,

  // Clickable
  cursor: 'pointer',

  // Animations
  opacity: isPlaying ? 0 : 1,
  transition: 'opacity 200ms ease-in-out',
  '&:hover': {
    opacity: 1,
  }
}));

/** Gradient background for control bar (play/pause) */
const ControlsBackground = styled(Box)({
  // Bottom section of the video
  position: 'absolute',
  bottom: '0',
  left: '0',
  width: '100%',
  height: '60px',

  // Black to transparent white gradient
  background: 'linear-gradient(0deg, rgba(0,0,0,1) 0%, rgba(255,255,255,0) 100%)',
});

/** Layout of control bar items */
const ControlsLayout = styled(Box)({
  // Middle of controls section
  width: '100%',
  height: '100%',
  padding: '28px 12px 12px 8px',

  // Separate left/right controls, vertically centered
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

/** Gradient overlay with replay icon */
const ReplayOverlay = styled(Box)({
  // Cover the video
  width: '100%',
  height: '100%',

  // Dark background
  background: 'rgba(0,0,0,.4)',

  // Center replay icon
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  // Animations
  animation: `${fadeInAnimation} 200ms ease-in-out forwards`,
});

/** material-icon wrapper */
const MaterialIcon = ({ children, ...rest }) => (
  <StyledIcon
    className='material-icons'
    {...rest}
  >
    {children}
  </StyledIcon>
)
const StyledIcon = styled(Box)({
  cursor: 'pointer',
  userSelect: 'none'
});
StyledIcon.defaultProps = { as: 'i' };
